import React from 'react';
import NonAIApplicationsTable from './NonAIApplicationsTable';
import type { NonAIDataResult } from '../types';
import { formatDecimalHours } from '../services/personalizedDataService';
import ExportButtons from './ExportButtons';
import { exportNonAIAppsToCSV, exportNonAIAppsToPDF, NonAIAppsExportOptions } from '../services/nonAIAppsExportService';

interface NonAIAppsSectionProps {
  nonAIData: NonAIDataResult;
  loading: boolean;
  error: string | null;
  viewMode: string;
  exportOptions?: NonAIAppsExportOptions;
}

const NonAIAppsSection: React.FC<NonAIAppsSectionProps> = ({ 
  nonAIData, 
  loading, 
  error, 
  viewMode,
  exportOptions
}) => {
  console.log('📱 [NON-AI-SECTION] Rendering with data:', {
    loading,
    error,
    viewMode,
    nonAIData: nonAIData ? 'exists' : 'null',
    teamMembersCount: nonAIData?.teamMemberApplications?.length || 0,
    topAppsCount: nonAIData?.topApplications?.length || 0,
    topApps: nonAIData?.topApplications?.slice(0, 3).map(app => app.applicationName) || []
  });
  // Show message if basic mode is selected
  if (viewMode === 'basic') {
    return (
      <div className="ai-overview-table-container">
        <div className="ai-overview-header">
          <h3>📱 Non-AI Applications Overview</h3>
          <p>Non-AI application data requires team access. Please switch to Central PS or My Team mode.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ai-overview-table-container">
        <div className="ai-overview-header">
          <h3>📱 Non-AI Applications Overview</h3>
          <p>Loading non-AI application data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-overview-table-container">
        <div className="ai-overview-header">
          <h3>📱 Non-AI Applications Overview</h3>
          <p>Failed to load non-AI data: {error}</p>
        </div>
      </div>
    );
  }

  const hasData = (nonAIData?.teamMemberApplications?.length || 0) > 0 || (nonAIData?.topApplications?.length || 0) > 0;

  if (!hasData) {
    return (
      <div className="ai-overview-table-container">
        <div className="ai-overview-header">
          <h3>📱 Non-AI Applications Overview</h3>
          <p>No non-AI application data available for the selected period.</p>
        </div>
        <div className="no-data-message">
          No team members have recorded non-AI application usage in the selected time period.
        </div>
      </div>
    );
  }

  const totalUsers = nonAIData?.teamMemberApplications?.length || 0;
  const totalNonAIHours = nonAIData?.teamMemberApplications?.reduce((sum, member) => sum + member.totalNonAIHours, 0) || 0;
  const totalApplications = [...new Set((nonAIData?.teamMemberApplications || []).flatMap(member => Object.keys(member.applications)))].length;

  /**
   * Handle CSV export
   */
  const handleExportCSV = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for Non-AI Apps CSV export');
      return;
    }
    exportNonAIAppsToCSV(nonAIData, exportOptions);
  };

  /**
   * Handle PDF export
   */
  const handleExportPDF = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for Non-AI Apps PDF export');
      return;
    }
    exportNonAIAppsToPDF(nonAIData, exportOptions);
  };

  return (
    <div className="ai-overview-table-container">
      <div className="ai-overview-header">
        <div>
          <h3>📱 Non-AI Applications Overview</h3>
          <p>Team non-AI application usage from Crossover activity tracking</p>
        </div>
        
        {exportOptions && hasData && (
          <ExportButtons
            onExportCSV={handleExportCSV}
            onExportPDF={handleExportPDF}
            disabled={!hasData}
            className="non-ai-export-container"
          />
        )}
      </div>

      <div className="ai-overview-stats">
        <div className="stats-section">
          <div className="stat-item">
            <span className="stat-label">Total Users:</span>
            <span className="stat-value">{totalUsers}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Non-AI Time:</span>
            <span className="stat-value">{formatDecimalHours(totalNonAIHours)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Unique Applications:</span>
            <span className="stat-value">{totalApplications}</span>
          </div>
        </div>

        {(nonAIData?.topApplications?.length || 0) > 0 && (
          <div className="most-used-apps-inline">
            <h4>🔥 Most Used Non-AI Applications</h4>
            <div className="app-usage-grid-inline">
              {(nonAIData?.topApplications || []).slice(0, 6).map((app, index) => (
                <div key={app.applicationName} className="app-usage-item-inline">
                  <span className="app-rank">#{index + 1}</span>
                  <span className="app-name">{app.applicationName}</span>
                  <span className="app-stats">
                    {formatDecimalHours(app.totalHours)} • {app.totalUsers} users
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="table-wrapper">
        <NonAIApplicationsTable teamMembers={nonAIData?.teamMemberApplications || []} />
      </div>
    </div>
  );
};

export default NonAIAppsSection;
